<template>
  <div class="row">
    <!-- Flash banner (fixed overlay, no layout shift) -->
    <transition name="flash">
      <div
        v-if="flash.show"
        class="flash-banner-fixed"
        :class="flashClass"
        role="status"
        aria-live="polite"
      >
        {{ flash.message }}
      </div>
    </transition>

    <div
      class="auth-viewport vh-100 align-items-center d-flex bg-white rounded-3 overflow-hidden"
    >
      <div class="card shadow-none border-0 ms-auto me-auto login-card w-100">
        <div class="card-body rounded-0 text-left p-3">
          <h2 class="fw-700 display1-size display2-md-size mb-4">
            Login To Your Account
          </h2>

          <form @submit.prevent="onSubmit" novalidate autocomplete="off">
            <!-- EMAIL (identifier) -->
            <div class="form-group icon-input mt-3">
              <i class="font-sm ti-email text-theme-2 pe-0"></i>
              <input
                ref="emailRef"
                type="email"
                id="loginEmail"
                class="style2-input ps-5 form-control text-theme-2 font-xsss fw-600"
                :class="{ 'invalid-input': showErr('email') }"
                placeholder="Your Email Address"
                v-model.trim="form.email"
                @blur="touch('email')"
                autocomplete="off"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                inputmode="email"
                name="user_email_input"
                aria-describedby="emailHelp loginEmailError"
                aria-invalid="true"
              />
            </div>
            <small id="emailHelp" class="text-theme-1 d-block mb-1">
              Use the email you registered with.
            </small>
            <p v-if="showErr('email')" id="loginEmailError" class="field-error">
              {{ errors.email }}
            </p>

            <!-- PASSWORD -->
            <div class="form-group icon-input mt-3">
              <input
                ref="passwordRef"
                type="password"
                id="loginPassword"
                class="style2-input ps-5 form-control text-theme-2 font-xss ls-3"
                :class="{ 'invalid-input': !!errors.password }"
                placeholder="Password"
                v-model="form.password"
                autocomplete="off"
                name="user_pass_input"
                aria-describedby="passwordHelp loginPasswordError"
                aria-invalid="true"
              />
              <i class="font-sm ti-lock text-theme-2 pe-0"></i>
            </div>
            <small id="passwordHelp" class="text-theme-1 d-block mb-1">
              Enter your account password.
            </small>
            <p
              v-if="errors.password"
              id="loginPasswordError"
              class="field-error"
            >
              {{ errors.password }}
            </p>

            <!-- REMEMBER / FORGOT -->
            <div class="form-check text-left mb-3 mt-2">
              <input
                type="checkbox"
                class="form-check-input mt-2"
                id="rememberMe"
                v-model="form.remember"
              />
              <label
                class="form-check-label font-xsss text-grey-500"
                for="rememberMe"
              >
                Remember me
              </label>
              <a
                href="#"
                class="fw-600 font-xsss text-grey-700 mt-1 float-right"
              >
                Forgot your Password?
              </a>
            </div>

            <!-- GLOBAL INLINE ERROR (fallback) -->
            <p v-if="submitError" class="field-error mb-2">{{ submitError }}</p>

            <div class="col-sm-12 p-0 text-left">
              <div class="form-group mb-1">
                <button
                  type="submit"
                  class="form-control text-center style2-input text-white fw-600 bg-dark border-0 p-0"
                  :disabled="loading"
                >
                  <span v-if="loading">Please wait…</span>
                  <span v-else>Login</span>
                </button>
              </div>
              <h6 class="text-grey-500 font-xsss fw-500 mt-0 mb-0 lh-32">
                Don't have an account?
                <router-link :to="{ name: 'register' }" class="fw-700 ms-1"
                  >Register</router-link
                >
              </h6>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onBeforeUnmount, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import http from "@/lib/http";
import { apiGetMyProfile } from "@/services/profileApi"; // fetch profile after login
import { avatarUrlFromProfile } from "@/utils/cdn"; // build CDN URL for warm-cache

const route = useRoute();
const router = useRouter();

/** Refs (used to nuke any browser-persisted values on mount) */
const emailRef = ref(null);
const passwordRef = ref(null);

const form = reactive({ email: "", password: "", remember: false });
const touched = reactive({ email: false });
const errors = reactive({ email: "", password: "" });

const loading = ref(false);
const submitError = ref("");

/** Flash banner (with auto-hide) */
const flash = reactive({ show: false, type: "info", message: "" });
const flashClass = computed(() => ({
  "flash-banner": true,
  "flash-success": flash.type === "success",
  "flash-error": flash.type === "error",
  "flash-info": flash.type === "info",
}));

let hideTimer = null;
function showFlash(type, message) {
  flash.type = type;
  flash.message = message;
  flash.show = true;
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    flash.show = false;
  }, 3000);
}
onBeforeUnmount(() => hideTimer && clearTimeout(hideTimer));

/** Tiny helper: detect if localStorage is writable (in-app browsers / private modes can block it) */
function storageCanWrite() {
  try {
    const k = "__sxt__";
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    return true;
  } catch (e) {
    console.warn("[STORAGE] localStorage not writable:", e);
    return false;
  }
}

/** On mount: show post-register message, and clear any prefilled values */
onMounted(() => {
  if (route.query?.registered === "1") {
    showFlash("success", "Account created. Login now.");
    // clean query so it won't reappear
    router.replace({ name: "login" });
  }

  // Hard clear values to fight autofill/prefill
  form.email = "";
  form.password = "";
  if (emailRef.value) emailRef.value.value = "";
  if (passwordRef.value) passwordRef.value.value = "";
});

/** Validation: EMAIL ONLY */
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function touch(field) {
  if (field === "email") {
    touched.email = true;
    validateEmail();
  }
}
function showErr(field) {
  return field === "email" ? touched.email && !!errors.email : false;
}
function validateEmail() {
  if (!form.email) errors.email = "Email is required.";
  else if (!emailRegex.test(form.email))
    errors.email = "Enter a valid email address.";
  else errors.email = "";
}

/** Submit -> POST /auth/login */
async function onSubmit() {
  submitError.value = "";
  validateEmail();
  if (errors.email) {
    submitError.value = "Please fix the highlighted fields.";
    showFlash("error", submitError.value);
    return;
  }

  loading.value = true;
  errors.password = ""; // reset server-driven password errors

  try {
    const payload = {
      identifier: form.email.trim().toLowerCase(), // backend accepts handle OR email; we pass email
      password: form.password,
    };

    console.log("[LOGIN] submitting payload for", payload.identifier);
    const { data } = await http.post("/auth/login", payload);
    console.log("[LOGIN] response OK (status 200)");

    const body = data && (data.user || data.tokens) ? data : data?.data;
    const user = body?.user;
    const tokens = body?.tokens;

    if (!user || !tokens?.accessToken || !tokens?.refreshToken) {
      console.error("[LOGIN] unexpected response shape:", body);
      throw new Error("Unexpected response from server.");
    }
    console.log("[LOGIN] user/tokens present:", {
      userId: user.id,
      handle: user.handle,
      atPreview: tokens.accessToken.slice(0, 12),
    });

    // Check storage availability before writing
    if (!storageCanWrite()) {
      showFlash(
        "error",
        "Your browser storage is blocked. Please allow storage or use a different browser."
      );
      console.warn("[LOGIN] storage blocked → aborting navigation");
      return;
    }

    // Persist user + tokens (write + read-back confirm)
    try {
      localStorage.setItem("user_email", user.email || "");
      localStorage.setItem("user_handle", user.handle || "");
      localStorage.setItem("access_token", tokens.accessToken);
      localStorage.setItem("refresh_token", tokens.refreshToken);
      // Also set the key your axios interceptor uses for Authorization
      localStorage.setItem("auth_token", tokens.accessToken);
      console.log("[LOGIN] stored tokens; auth_token exists:", !!localStorage.getItem("auth_token"));
    } catch (err) {
      console.warn("[LOGIN] localStorage set failed:", err);
      showFlash(
        "error",
        "Could not save session. Please disable private mode or try another browser."
      );
      return;
    }

    // Prefetch & cache profile immediately after token is set
    try {
      const profile = await apiGetMyProfile(); // normalized plain object
      localStorage.setItem("profile_details", JSON.stringify(profile));

      // --- Warm the browser's HTTP cache for the avatar URL ---
      const url = avatarUrlFromProfile(profile);
      if (url) {
        const link = document.createElement("link");
        link.rel = "preload"; // or 'prefetch'
        link.as = "image";
        link.href = url;
        document.head.appendChild(link);

        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = url;
      }
      console.log("[LOGIN] profile prefetched and avatar warmed");
      // --- END warm-cache block ---
    } catch (e) {
      console.warn("Prefetch profile after login failed:", e?.message || e);
    }

    // Redirect to home/feed with robust fallback
    const target = route.query.redirect || { name: "home" };
    console.log("[LOGIN] navigating to", target);

    router.push(target).then(() => {
      console.log(
        "[LOGIN] router.push resolved → now at",
        router.currentRoute.value.fullPath
      );

      // Fallback: if still on login after a tick, hard-navigate (covers buggy WebViews)
      setTimeout(() => {
        if (router.currentRoute.value.name === "login") {
          console.warn("[LOGIN] still on login after push → hard redirect fallback");
          window.location.assign(typeof target === "string" ? target : "/");
        }
      }, 300);
    });
  } catch (e) {
    const status = e?.response?.status;
    const msg =
      (e?.response?.data &&
        (e.response.data.message || e.response.data.error)) ||
      e?.message ||
      "Something went wrong.";

    console.warn("[LOGIN] error", { status, msg });

    if (status === 401) {
      // invalid credentials (generic)
      errors.password = "Invalid credentials.";
      showFlash("error", "Invalid credentials.");
      return;
    }

    if (status === 403) {
      showFlash("error", "Your account is suspended.");
      return;
    }

    if (status === 400 && /password required/i.test(msg)) {
      errors.password = "Password is required.";
      showFlash("error", "Please enter your password.");
      return;
    }

    showFlash("error", msg || "Server error. Please try again.");
  } finally {
    loading.value = false;
  }
}
</script>


<style scoped>
/* Enable scrolling when content exceeds viewport */
.auth-viewport {
  height: auto !important;
  min-height: 100vh;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  padding-block: 16px;
}

/* Flash banner (fixed overlay, no layout shift) */
.flash-banner-fixed {
  position: fixed;
  top: 100px; /* match RegisterView */
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 2000;
  padding: 10px 15px;
  width: min(400px, 90vw);
  pointer-events: none;
  text-align: center;
}
.flash-banner {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.flash-success {
  background: #eafaf1;
  color: #1e7e34;
  border: 1px solid #b6efcc;
}
.flash-error {
  background: #feeeee;
  color: #b00020;
  border: 1px solid #f6bcbc;
}
.flash-info {
  background: #eef5ff;
  color: #1b5dab;
  border: 1px solid #c7dcff;
}

/* Slide-down/up transition */
.flash-enter-from,
.flash-leave-to {
  transform: translate(-50%, -16px);
  opacity: 0;
}
.flash-enter-active,
.flash-leave-active {
  transition: transform 200ms ease, opacity 200ms ease;
}
.flash-enter-to,
.flash-leave-from {
  transform: translate(-50%, 0);
  opacity: 1;
}

/* Validation cues */
.invalid-input {
  border-color: #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.08);
}
.field-error {
  margin: 4px 2px 8px;
  font-size: 12px;
  line-height: 1.3;
  color: #d63031;
}
.small {
  font-size: 12px !important;
}
</style>
